'use client'

import useLocalStorageState from '@components/hooks/useLocalStorageState'
import { CheckboxMultiSelect } from '@components/primitives/HtmlForm/CheckboxMultiSelect.client'
import { Text } from '@components/primitives/Text'
import { NO_CONDITIONS_VALUE } from '@lib/questionnaire/trialApplication/generatePrescreeningQuestionConfig'
import uniqBy from 'lodash/uniqBy'
import { useState } from 'react'
import { createConditionSelectOptions } from '../ConditionSelect'
import { debounceTrackQuestionModified } from './trackQuestionModified'
import type { DemographicsCollectElementProps } from './types'

const debouncedEventTracking = debounceTrackQuestionModified('conditions')

export default function CollectConditions({
  conditionsFromTrial,
  formRef,
  patientProfileData: { conditions },
  questionConfig,
  trialAcceptsHealthyVolunteers,
}: DemographicsCollectElementProps) {
  // conditions from the profile is colloquial term, but the "value" in the selectbox is the condition "name"
  const conditionValues = conditions.map(
    (c) =>
      conditionsFromTrial.find((ct) => ct.colloquialTermStripped === c)?.name ??
      c,
  )
  const [currentValue, setCurrentValue] = useState(conditionValues)
  const [currentLocalStorageValue, setLocalStorageValue] = useLocalStorageState(
    'trialApplicationV2Metadata',
    {},
  )

  // we do not want duplicate condition colloqualTerms
  const conditionsAsItems = uniqBy(
    [
      ...createConditionSelectOptions(
        conditionsFromTrial,
        trialAcceptsHealthyVolunteers,
      ),
      { label: NO_CONDITIONS_VALUE, value: NO_CONDITIONS_VALUE },
    ].flatMap((item) => (item ? [item] : [])),
    (item) => item.value,
  )

  const hasSingleCondition =
    conditionsAsItems.length === 2 &&
    conditionsAsItems.some((item) => item.label === NO_CONDITIONS_VALUE)

  const handleInputChange = (newInputValue: string[]) => {
    debouncedEventTracking(newInputValue)
    setCurrentValue(newInputValue)

    // if conditions list has only one condition and "None", then submit form on single selection
    if (hasSingleCondition && newInputValue.length === 1) {
      setLocalStorageValue({
        ...(currentLocalStorageValue ?? {}),
        selectedCondition: newInputValue[0],
      })

      setTimeout(() => {
        formRef?.current?.requestSubmit()
      })
    }
  }

  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element={'p'}
        styleName='h3'
        value='Which conditions do you have?'
      />
      <Text
        className='text-neutral900'
        element={'p'}
        styleName='p-small'
        value='Why we ask: to provide you with more accurate recommendations of other relevant trials.'
      />
      <div className='grid grid-cols-1 gap-2'>
        <CheckboxMultiSelect
          className='px-32'
          defaultValues={currentValue}
          formRef={formRef}
          name={questionConfig.id}
          onChange={handleInputChange}
          options={conditionsAsItems}
          required={!questionConfig.skippable}
        />
      </div>
    </div>
  )
}
